import { useState, useEffect } from "react";
import { api } from "../api/client";
import type { ChainId } from "../api/types";
import { formatTokenAmount } from "../lib/formatTokenAmount";

type TokenBalance = { amount: string; decimals: number; uiAmount: number };

type Validation =
  | {
      index: number;
      ok: true;
      pubkey: string;
      solBalance: number | null;
      tokenBalance?: TokenBalance | null;
    }
  | { index: number; ok: false; error: string };

export function WalletsInput({
  chain = "solana",
  mint,
  value,
  onChange,
}: {
  chain?: ChainId;
  /** Optional: target-token mint. When provided, validate endpoint also returns
   *  per-wallet token balance and the UI displays it next to native balance. */
  mint?: string;
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  const [validations, setValidations] = useState<Validation[]>([]);

  useEffect(() => {
    const keys = value
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (keys.length === 0) {
      setValidations([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await api.post<{ wallets: Validation[] }>("/api/wallets/validate", {
          keys,
          chain,
          mint: mint && mint.length > 0 ? mint : undefined,
        });
        if (!cancelled) setValidations(res.wallets);
      } catch {
        // silent
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [value, chain, mint]);

  const isEvm = chain === "base" || chain === "ethereum" || chain === "robinhood" || chain === "bnb" || chain === "ink" || chain === "arc";
  const unit = chain === "bnb" ? "BNB" : chain === "arc" ? "USDC" : isEvm ? "ETH" : "SOL";

  const okCount = validations.filter((v) => v.ok).length;
  const errCount = validations.length - okCount;
  const totalBalance = validations.reduce(
    (sum, v) => (v.ok && v.solBalance !== null ? sum + v.solBalance : sum),
    0,
  );
  const tokenBalances = validations.flatMap((v) =>
    v.ok && v.tokenBalance ? [v.tokenBalance] : [],
  );
  const tokenDecimals = tokenBalances[0]?.decimals;
  const totalTokenRaw = tokenBalances.reduce(
    (sum, balance) =>
      balance.decimals === tokenDecimals ? sum + BigInt(balance.amount) : sum,
    0n,
  );
  const anyTokenBalance = validations.some((v) => v.ok && v.tokenBalance);

  return (
    <div>
      <textarea
        className="input font-mono text-xs h-32 resize-y"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={
          isEvm
            ? "Paste 0x-prefixed hex private keys, one per line"
            : "Paste base58 secret keys, one per line"
        }
      />
      {validations.length > 0 && (
        <div className="mt-2 text-xs">
          <div className="flex gap-4 mb-1 flex-wrap">
            <span className="text-ok">{okCount} valid</span>
            {errCount > 0 && <span className="text-err">{errCount} invalid</span>}
            {okCount > 0 && (
              <span className="text-muted">
                total <span className="text-fg">{totalBalance.toFixed(4)}</span> {unit}
              </span>
            )}
            {anyTokenBalance && (
              <span className="text-muted">
                token{" "}
                <span className="text-fg">
                  {formatTokenAmount(totalTokenRaw.toString(), tokenDecimals ?? 0)}
                </span>
              </span>
            )}
          </div>
          <ul className="font-mono text-[11px] text-muted space-y-0.5 max-h-32 overflow-y-auto">
            {validations.map((v) =>
              v.ok ? (
                <li key={v.index}>
                  <span className="text-fg">#{v.index + 1}</span> → {v.pubkey}{" "}
                  <span className="text-fg">
                    {v.solBalance === null ? "—" : `${v.solBalance.toFixed(4)} ${unit}`}
                  </span>
                  {v.tokenBalance && (
                    <span className="text-muted ml-2">
                      |{" "}
                      <span className="text-fg">
                        {formatTokenAmount(v.tokenBalance.amount, v.tokenBalance.decimals)}
                      </span>{" "}
                      tok
                    </span>
                  )}
                </li>
              ) : (
                <li key={v.index} className="text-err">
                  #{v.index + 1} invalid: {v.error}
                </li>
              ),
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
